import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import AITxt from "../assets/img/new-ui/ai-magic-txt-new.png";
import AITxtTransformation from "../assets/img/new-ui/final-touch-text-1.png";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { setError, setLoading, setProcessedImageUrl, setUploadedImageUrl } from "../redux/slices/uploadFlowSlice";
import { buildProcessImageParamsFromArtAndPreset, processImageWithRetry } from "../services/processImageService";
import {
    effectiveStyleSlugForAiSelfieArt,
    getProcessPresetForSlug,
    getTransformCustomLineFontFamily,
    getTransformCustomLineMaxLength,
    isCaricatureHobbyAiSelfieArt,
    isSnapPrintFlow,
    resolveHobbyCaricatureRouteSlug,
    shouldUsePetCaricatureBackgroundFlow,
} from "../services/stylePromptResolver";
import { drawUserLineOnImage } from "../utils/drawUserLineOnImage";
import type { AiSelfieArt } from "../services/aiSelfieArtService";
import StationThemeLayout from "../layouts/StationThemeLayout";
import SignOutConfirmModal from "../modal/SignOutConfirmModal";
import { readActiveStoredStation, resolveStationRouteSlug } from "../utils/stationFlowStorage";
import { persistHobbyCharacterNologoPairs } from "../utils/hobbyGenerationNavigation";
import { readFavouriteTransparencyFlag } from "../services/caricatureHobbyService";
import { Open } from "../compoment/icon";
import Loader from "../compoment/ui/Loader";

/** Caption sits on top for Andy Warhol Marilyn styles only; others stay bottom. */
function userLinePositionForSlug(slug: string | undefined): "top" | "bottom" {
    const s = (slug || "").trim().toLowerCase();
    if (
        s === "andy-warhol-marilyn-monroe-style" ||
        s === "andy-warhol-s-marilyn-monroe-style"
    ) {
        return "top";
    }
    return "bottom";
}

const BLUR_DELAY_AFTER_PROMPT_MS = 1000;
const BLUR_HOLD_MS = 2500;
const MAGIC_ANIMATION_MS = 4500;
const PROMPT_EXIT_MS = 500;

const preloadImage = (url: string): Promise<void> =>
    new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = url;
        if (img.complete) resolve();
    });

type TransformationPhase = 1 | 2 | 3 | 4;

interface ImageTransformationScreenProps {
    /** Optional override; defaults to `:slug` from the route */
    styleSlug?: string;
}

const ImageTransformationScreen: React.FC<ImageTransformationScreenProps> = ({ styleSlug: styleSlugProp }) => {
    const { slug: slugParam } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        stationData,
        skipPrompt,
        bgRemoveDecided,
        bgRemoved: bgRemovedFromNav,
        uploadedImageUrl: uploadedImageUrlFromNav,
    } = (location.state || {}) as {
        stationData?: { backgroundImageUrl?: string; type?: string; name?: string };
        skipPrompt?: boolean;
        /** Print Your Own: already answered Yes/No on /bg-remove — start generate. */
        bgRemoveDecided?: boolean;
        /** Print Your Own: user chose Yes — cutout + is_transparent. */
        bgRemoved?: boolean;
        uploadedImageUrl?: string;
    };
    const forceTransparent =
        bgRemovedFromNav === true ||
        (bgRemoveDecided === true && readFavouriteTransparencyFlag());
    const [phase, setPhase] = useState<TransformationPhase>(1);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [customLine, setCustomLine] = useState("");
    const [promptConfirmed, setPromptConfirmed] = useState(false);
    const [promptLeaving, setPromptLeaving] = useState(() => Boolean(skipPrompt));
    const [showBackConfirmModal, setShowBackConfirmModal] = useState(false);
    const selectedAiSelfieArt = useMemo((): Partial<AiSelfieArt> | null => {
        try {
            const raw = localStorage.getItem("selectedAiSelfieArt");
            if (!raw) return null;
            return JSON.parse(raw) as Partial<AiSelfieArt>;
        } catch {
            return null;
        }
    }, []);

    const styleSlug =
        styleSlugProp ||
        resolveStationRouteSlug(stationData, slugParam, selectedAiSelfieArt) ||
        "pencil-sketch";
    const dispatch = useDispatch();
    const { uploadedImageUrl } = useSelector((s: RootState) => s.uploadFlow || { uploadedImageUrl: null });

    // After /bg-remove, sync cutout URL from navigation into Redux before generate.
    useEffect(() => {
        const fromNav =
            typeof uploadedImageUrlFromNav === "string" ? uploadedImageUrlFromNav.trim() : "";
        if (!fromNav || fromNav === uploadedImageUrl) return;
        dispatch(setUploadedImageUrl(fromNav));
    }, [dispatch, uploadedImageUrlFromNav, uploadedImageUrl]);

    const processingRef = useRef(false);
    const phaseRef = useRef<TransformationPhase>(1);
    const apiReadyRef = useRef(false);
    const exitStartedRef = useRef(false);
    const submittedLineRef = useRef("");
    const promptExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const navTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const recoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isPromptStep = !promptConfirmed;

    useEffect(() => {
        if (isCaricatureHobbyAiSelfieArt(selectedAiSelfieArt)) {
            const hobbySlug =
                resolveHobbyCaricatureRouteSlug(styleSlug) || styleSlug || "hobby-caricature";
            navigate(`/${hobbySlug}/select-people`, { replace: true });
        }
    }, [navigate, selectedAiSelfieArt]);

    const presetSlug = useMemo(
        () => effectiveStyleSlugForAiSelfieArt(selectedAiSelfieArt, styleSlug) ?? styleSlug,
        [selectedAiSelfieArt, styleSlug]
    );

    const preset = useMemo(() => getProcessPresetForSlug(presetSlug), [presetSlug]);
    const customLineMaxLength = useMemo(
        () =>
            getTransformCustomLineMaxLength(
                presetSlug,
                styleSlug,
                selectedAiSelfieArt?.name
            ),
        [presetSlug, selectedAiSelfieArt?.name, styleSlug]
    );

    const imageSrc = uploadedImageUrl || "" as any;

    const isSnapPrint = useMemo(
        () =>
            isSnapPrintFlow({
                slug: styleSlug,
                stationType: stationData?.type || readActiveStoredStation()?.type,
                artName: selectedAiSelfieArt?.name,
            }),
        [styleSlug, stationData?.type, selectedAiSelfieArt?.name]
    );

    const goToResult = useCallback(() => {
        if (exitStartedRef.current) return;
        exitStartedRef.current = true;
        // Stop zoom animations immediately, then leave this screen with no
        // carry-over fade into the next step.
        setIsExiting(true);
        setPhase(1);
        phaseRef.current = 1;
        // Pet caricature only — every other station still goes to transformation-result.
        if (shouldUsePetCaricatureBackgroundFlow(selectedAiSelfieArt, stationData)) {
            navigate(`/${styleSlug}/select-art-background`, {
                state: { stationData },
            });
            return;
        }
        navigate(`/${styleSlug}/transformation-result`, { state: { stationData } });
    }, [navigate, selectedAiSelfieArt, stationData, styleSlug]);

    // Recover from a missing upload or a failed transformation by returning to
    // the upload screen instead of leaving the kiosk stuck on the animation.
    const recoverToUpload = useCallback(() => {
        if (exitStartedRef.current) return;
        exitStartedRef.current = true;
        navigate(`/${styleSlug}/upload-image-qr-code`, { replace: true });
    }, [navigate, styleSlug]);

    const tryNavigateWhenReady = useCallback(() => {
        if (!apiReadyRef.current || exitStartedRef.current) return;
        // Print Your Own: plain loader — leave as soon as generate finishes.
        // Other stations: wait until the second magic image is showing.
        if (!isSnapPrint && phaseRef.current < 4) return;
        goToResult();
    }, [goToResult, isSnapPrint]);

    const setPhaseSafe = useCallback((next: TransformationPhase) => {
        phaseRef.current = next;
        setPhase(next);
        if (next === 4) {
            tryNavigateWhenReady();
        }
    }, [tryNavigateWhenReady]);

    const processImage = useCallback(async () => {
        if (!uploadedImageUrl || processingRef.current) return;

        processingRef.current = true;
        dispatch(setLoading(true));

        try {
            const processParams = buildProcessImageParamsFromArtAndPreset({
                imageurl: uploadedImageUrl,
                art: selectedAiSelfieArt,
                slug: styleSlug,
                preset,
                forceTransparent,
            });
            // The optional line is drawn onto the generated artwork afterwards,
            // so the generation request stays exactly the base art prompt.
            const submittedLine = submittedLineRef.current.trim();

            const resp = await processImageWithRetry(processParams);
            const respData = (resp as {
                data?: {
                    processedImageUrl?: string;
                    processedImageUrlNoLogo?: string;
                    image_url_nologo?: string;
                    output_url?: string;
                };
            })?.data;
            const generatedUrl = respData?.processedImageUrl || respData?.output_url;
            const generatedUrlNoLogo =
                (typeof respData?.processedImageUrlNoLogo === "string" &&
                    respData.processedImageUrlNoLogo.trim()) ||
                (typeof respData?.image_url_nologo === "string" &&
                    respData.image_url_nologo.trim()) ||
                "";

            const captionSlug = presetSlug || styleSlug;
            const lineDrawOpts = {
                position: userLinePositionForSlug(captionSlug),
                fontFamily: getTransformCustomLineFontFamily(
                    captionSlug,
                    selectedAiSelfieArt?.name
                ),
            };
            const outUrl = generatedUrl && submittedLine
                ? await drawUserLineOnImage(generatedUrl, submittedLine, lineDrawOpts)
                : generatedUrl;
            // Keep logo → no-logo map aligned when a custom caption is burnt into the art.
            const outUrlNoLogo =
                generatedUrlNoLogo && submittedLine
                    ? await drawUserLineOnImage(generatedUrlNoLogo, submittedLine, lineDrawOpts)
                    : generatedUrlNoLogo || undefined;

            if (resp && outUrl) {
                if (outUrlNoLogo) {
                    persistHobbyCharacterNologoPairs([outUrl], [outUrlNoLogo]);
                }
                dispatch(setProcessedImageUrl(outUrl));
                await preloadImage(outUrl);
                apiReadyRef.current = true;
                tryNavigateWhenReady();
            } else {
                dispatch(setError((resp as { error?: string })?.error || "Processing failed"));
                recoverTimerRef.current = setTimeout(recoverToUpload, 4000);
            }
        } catch (error: unknown) {
            dispatch(setError(error instanceof Error ? error.message : "Processing failed"));
            recoverTimerRef.current = setTimeout(recoverToUpload, 4000);
        } finally {
            dispatch(setLoading(false));
            processingRef.current = false;
        }
    }, [
        uploadedImageUrl,
        dispatch,
        selectedAiSelfieArt,
        styleSlug,
        presetSlug,
        preset,
        forceTransparent,
        tryNavigateWhenReady,
        recoverToUpload,
    ]);

    // After the prompt closes: wait one more second (two seconds from click),
    // blur for 2.5 seconds, play the first magic animation once, then leave the
    // second magic animation looping until the API response is ready.
    // Print Your Own skips this and uses a plain loader instead.
    useEffect(() => {
        if (!promptConfirmed || !imageLoaded || isSnapPrint) return;

        let timer: ReturnType<typeof setTimeout> | null = null;
        let cancelled = false;

        const scheduleMagicTwo = () => {
            if (cancelled) return;
            setPhaseSafe(4);
        };

        const scheduleMagicOne = () => {
            if (cancelled) return;
            setPhaseSafe(3);
            timer = setTimeout(scheduleMagicTwo, MAGIC_ANIMATION_MS);
        };

        timer = setTimeout(() => {
            setPhaseSafe(2);
            timer = setTimeout(scheduleMagicOne, BLUR_HOLD_MS);
        }, BLUR_DELAY_AFTER_PROMPT_MS);

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [promptConfirmed, imageLoaded, setPhaseSafe, isSnapPrint]);

    useEffect(() => {
        if (!promptConfirmed || !imageLoaded) return;
        processImage();
    }, [promptConfirmed, imageLoaded, processImage]);

    // Without an uploaded image the <img> never fires onLoad, so the phase
    // timers and processing never start. Redirect back to upload instead.
    useEffect(() => {
        if (uploadedImageUrl) return;
        recoverTimerRef.current = setTimeout(recoverToUpload, 1500);
        return () => {
            if (recoverTimerRef.current) clearTimeout(recoverTimerRef.current);
        };
    }, [uploadedImageUrl, recoverToUpload]);

    // Clear any pending navigation/recovery timers on unmount so we never call
    // navigate() after the user has already left this screen.
    useEffect(() => {
        return () => {
            if (navTimerRef.current) clearTimeout(navTimerRef.current);
            if (recoverTimerRef.current) clearTimeout(recoverTimerRef.current);
            if (promptExitTimerRef.current) clearTimeout(promptExitTimerRef.current);
        };
    }, []);

    const continueTransformation = (line?: string) => {
        if (promptLeaving || promptConfirmed) return;

        const normalizedLine = (line ?? "").trim().slice(0, customLineMaxLength);
        submittedLineRef.current = normalizedLine;
        setCustomLine(normalizedLine);
        if (normalizedLine) {
            localStorage.setItem("transformationCustomLine", normalizedLine);
        } else {
            localStorage.removeItem("transformationCustomLine");
        }

        // Print Your Own: ask BG remove Yes/No before generate animation.
        if (isSnapPrint && !bgRemoveDecided) {
            navigate(`/${styleSlug}/bg-remove`, {
                state: {
                    stationData: stationData || readActiveStoredStation(),
                    customLine: normalizedLine,
                    uploadedImageUrl: uploadedImageUrl || undefined,
                },
            });
            return;
        }

        setPromptLeaving(true);
        promptExitTimerRef.current = setTimeout(() => {
            setPromptConfirmed(true);
        }, PROMPT_EXIT_MS);
    };

    // Try again / after bg-remove: skip the prompt UI, but reuse any previously
    // submitted custom line so generation still applies the user's instruction.
    useEffect(() => {
        if ((!skipPrompt && !bgRemoveDecided) || promptConfirmed) return;

        const fromState = typeof (location.state as { customLine?: string } | null)?.customLine === "string"
            ? String((location.state as { customLine?: string }).customLine || "").trim()
            : "";
        const savedLine = (fromState || localStorage.getItem("transformationCustomLine") || "")
            .trim()
            .slice(0, customLineMaxLength);
        submittedLineRef.current = savedLine;
        setCustomLine(savedLine);
        setPromptLeaving(true);
        promptExitTimerRef.current = setTimeout(() => {
            setPromptConfirmed(true);
        }, PROMPT_EXIT_MS);
    }, [skipPrompt, bgRemoveDecided, promptConfirmed, customLineMaxLength, location.state]);

    // If the art type limit shrinks, keep the field within the allowed length.
    useEffect(() => {
        setCustomLine((prev) =>
            prev.length > customLineMaxLength ? prev.slice(0, customLineMaxLength) : prev
        );
        if (submittedLineRef.current.length > customLineMaxLength) {
            submittedLineRef.current = submittedLineRef.current.slice(0, customLineMaxLength);
        }
    }, [customLineMaxLength]);

    return (
        <StationThemeLayout
            className={`caricatureTransformationSection${isPromptStep ? " is-prompt-step" : " is-processing-step"}${promptLeaving ? " is-prompt-leaving" : ""}`}
            station={
                (stationData as { backgroundImageUrl?: string; type?: string; name?: string } | undefined) ||
                readActiveStoredStation()
            }
            showStationLogo
            showBack={!promptLeaving && !promptConfirmed}
            onBack={() => setShowBackConfirmModal(true)}
        >
            {/* Print Your Own: plain spinner while generate runs (no magic copy). */}
            {promptConfirmed && isSnapPrint && <Loader />}
            <div className="imgSketchContainer mt-5">
                <div className="imgSection imgSectionTransformation">
                    <div
                        className={`transformationStage transformationPhase-${promptConfirmed ? phase : 1}${isExiting ? " is-exiting" : ""}${isPromptStep ? " is-prompt-idle" : ""}`}
                        data-phase={promptConfirmed ? phase : 1}
                    >
                        <div className="transformationImageWrap">
                            <img
                                src={imageSrc}
                                alt={styleSlug}
                                className="caricatureImg transformationPreview"
                                onLoad={() => {
                                    setImageLoaded(true);
                                }}
                            />
                            {promptConfirmed && !isSnapPrint && (
                                <div className="transformationWhiteOverlay" aria-hidden="true" />
                            )}
                        </div>

                        {promptConfirmed && !isSnapPrint && (
                            <>
                                <div className="transformationMagicLayer transformationMagicLayer--1" aria-hidden={phase < 3}>
                                    <img src={AITxt} alt="Applying AI magic" className="caricatureImgTxtAIP" />
                                </div>

                                <div className="transformationMagicLayer transformationMagicLayer--2" aria-hidden={phase < 4}>
                                    <img src={AITxtTransformation} alt="Final touches in progress" className="caricatureImgTxtAIFinal" />
                                </div>
                            </>
                        )}
                    </div>

                    {isPromptStep && !skipPrompt && (
                        <div className={`transformationPromptStep${promptLeaving ? " is-leaving" : ""}`}>
                            <div className="transformationPromptHeading">
                            Add custom message to your photo<span>(Optional)</span>
                            </div>
                            <form
                                className="transformationPromptForm"
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    continueTransformation(customLine);
                                }}
                            >
                                <div className="transformationPromptField">
                                    <textarea
                                        value={customLine}
                                        placeholder="Add custom message to your photo"
                                        aria-label="Add custom message to your photo"
                                        maxLength={customLineMaxLength}
                                        disabled={promptLeaving}
                                        onChange={(event) =>
                                            setCustomLine(
                                                event.target.value.slice(0, customLineMaxLength)
                                            )
                                        }
                                    />
                                    <div className="transformationPromptCharCount" aria-live="polite">
                                        {customLine.length}/{customLineMaxLength}
                                    </div>
                                </div>
                                <button type="submit" disabled={promptLeaving}>
                                    Submit
                                </button>
                            </form>
                            <button
                                type="button"
                                className="transformationPromptSkip"
                                disabled={promptLeaving}
                                onClick={() => continueTransformation()}
                            >
                                Skip <span aria-hidden="true"><Open/></span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {showBackConfirmModal && (
                <SignOutConfirmModal
                    show={showBackConfirmModal}
                    onHide={() => setShowBackConfirmModal(false)}
                    onConfirm={() => {
                        setShowBackConfirmModal(false);
                        navigate("/");
                    }}
                    message="Are you sure you want to go back to the home screen?"
                    cancelLabel="No"
                    confirmLabel="Yes"
                />
            )}
        </StationThemeLayout>
    );
};

export default ImageTransformationScreen;
